import { ElementProps } from '@/components/sections/builder/types';
import {
  ImageWithTextElementDataType,
  ImageWithTextSubtitleElementDataType,
} from '@/components/sections/builder/elements/body/image-with-text/types';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

const defaultData: ImageWithTextSubtitleElementDataType = {
  name: 'Sub Title',
  label:
    'Pair text with an image to focus on your chosen product, collection, or blog post. Add details on availability, style, or even provide a review.',
  textStyle: 'subtitle',
  padding: {
    bottom: 16,
    top: 0,
    left: 0,
    right: 0,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ImageWithTextSubtitleElementDataType;
  const parentData = element.parentData as ImageWithTextElementDataType;

  return (
    <>
      <p
        className={clsx(
          'hero-subtitle-padding text_color_container break-words text-base font-normal ',
          data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
          data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
          data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base',
          parentData.contentAlign === 'left' && 'lg:text-left',
          parentData.contentAlign === 'center' && 'lg:text-center',
          parentData.contentAlign === 'right' && 'lg:text-right',
          parentData.contentAlignMobile === 'left' && 'text-left',
          parentData.contentAlignMobile === 'center' && 'text-center',
          parentData.contentAlignMobile === 'right' && 'text-right'
        )}
        data-animation='true'
        dangerouslySetInnerHTML={{ __html: data.label as string }}
      />
      <style jsx>
        {`
          @media (min-width: 1024px) {
            .hero-subtitle-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .text_color_container {
            color: ${parentData.containerColorScheme?.textSecondary || '#6b7280'};
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageWithTextSubtitleElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>Text</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            label: value,
          })
        }
        value={data.label}
        placeholder='Label'
        output='html'
        className='mb-2 w-full '
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Text Style</div>
      <InputTab
        defaultValue={data.textStyle}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            textStyle: v,
          })
        }
      />
    </div>
  );
}

const ImageWithTextSubtitle = {
  type: 'image_with_text_subtitle',
  group: 'image_with_text',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ImageWithTextSubtitle;
